import { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslate } from '@repo/unicity'
import { useCurrentLanguage } from '@/core/utils'
import { fgProductsAtom } from '@/core/feelgreat/feelgreat.store'
import { feelgreatCountryAtom } from '@/core/store/country.store'
import { getProductPrice, usePriceMode } from '@/core/cart/useCart'
import { useV1CartEntries } from '@/core/cart/useCartPricing'
import { useCartMutations } from '@/core/cart/useCartMutations'
import { cartAtom, cartEditedAtom, type CartItems } from '@/core/cart/cart.store'
import BinIcon from '@/core/assets/BinIcon.svg?react'
import TagIcon from '@/core/assets/TagIcon.svg?react'
import { color } from '@/core/theme/tokens'
import { taiwanFormatDataAtom, taiwanCartSyncingAtom } from '../../../stores/taiwan.store'
import { useTaiwanCartSync } from '../../../hooks/useTaiwanCartSync'

// Figma node 689:5570 — Order Summary panel. Raw hexes below are design values
// with no scale token (kept verbatim): #0b3b61 title, #003764 savings text,
// #009ecf discount, #e9f1fa qty-stepper bg.
const SUMMARY_TITLE = '#0b3b61'
const SAVINGS_TEXT = '#003764'
const DISCOUNT = '#009ecf'
const QTY_BG = '#e9f1fa'

const formatMoney = (symbol: string, n: number): string => `${symbol}${Math.round(n).toLocaleString('en-US')}`

const asRecord = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {})
const localize = (v: unknown, language: string): string => {
    const r = asRecord(v)
    const preferred = language.toUpperCase() === 'EN' ? r.english : r.native
    return (typeof preferred === 'string' && preferred) || (typeof r.english === 'string' ? r.english : '')
}

// Server-calculated freight for the active session (flat $150 for TW).
const readShipping = (format: Record<string, unknown> | null): number => {
    const marketing = asRecord(asRecord(asRecord(format ?? {}).hydra).marketing)
    const freight = asRecord(asRecord(marketing.terms).freight)
    return typeof freight.amount === 'number' ? freight.amount : 0
}

// The v1 cart shape IS the format's `shipment_packs` JSON, so we can rebuild
// the (non-persisted) cartAtom from the loaded format after a hard refresh.
const readShipmentPacks = (format: Record<string, unknown> | null): CartItems | null => {
    const packs = asRecord(asRecord(asRecord(format ?? {}).ushop).detail_page).shipment_packs
    if (typeof packs !== 'string') return null
    try {
        const parsed = JSON.parse(packs)
        return parsed && typeof parsed === 'object' ? (parsed as CartItems) : null
    } catch {
        return null
    }
}

// Qty stepper — Figma 689:6157: bg #e9f1fa, radius 20, 12/4 padding, width 72.
const QtyControls = ({ qty, onIncrease, onDecrease }: { qty: number; onIncrease: () => void; onDecrease: () => void }) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ bgcolor: QTY_BG, borderRadius: '20px', px: '12px', py: '4px', width: 72 }}
        data-testid="taiwan-summary-qty">
        <StepBtn label="−" disabled={qty <= 1} onClick={onDecrease} testid="taiwan-summary-qty-dec" />
        <Typography
            sx={{ width: 32, textAlign: 'center', fontSize: 14, lineHeight: '20px', fontWeight: 500, color: color.blue[600] }}
            data-testid="taiwan-summary-qty-value">
            {qty}
        </Typography>
        <StepBtn label="+" disabled={false} onClick={onIncrease} testid="taiwan-summary-qty-inc" />
    </Stack>
)

const StepBtn = ({ label, disabled, onClick, testid }: { label: string; disabled: boolean; onClick: () => void; testid: string }) => (
    <Box
        onClick={() => {
            if (!disabled) onClick()
        }}
        sx={{
            width: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 600,
            userSelect: 'none',
            color: disabled ? color.gray[400] : color.blue[600],
            cursor: disabled ? 'not-allowed' : 'pointer'
        }}
        data-testid={testid}>
        {label}
    </Box>
)

// Figma 26:154888 — white card, radius 12, no border, padding 20 (both breakpoints).
const cardSx = { borderRadius: '12px', p: '20px', bgcolor: color.white }

// Placeholder shown while a cart edit re-syncs (server recalculates prices).
const SummarySkeleton = () => (
    <Stack spacing={2.5} data-testid="taiwan-summary-skeleton">
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Skeleton variant="rounded" width={64} height={64} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={22} />
                <Skeleton variant="text" width="40%" height={18} />
            </Box>
        </Stack>
        <Divider />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="100%" height={20} />
        <Divider />
        <Skeleton variant="text" width="55%" height={26} />
        <Skeleton variant="rounded" width="100%" height={72} />
    </Stack>
)

export default function TaiwanOrderSummary({ readOnly = false, footer }: { readOnly?: boolean; footer?: React.ReactNode } = {}) {
    const { translate } = useTranslate()
    const language = useCurrentLanguage()
    const priceMode = usePriceMode()
    const fgProducts = useAtomValue(fgProductsAtom)
    const fgCountry = useAtomValue(feelgreatCountryAtom)
    const format = useAtomValue(taiwanFormatDataAtom)
    const entries = useV1CartEntries(priceMode)
    const shipping = readShipping(format)

    // Currency symbol from the country config (TWN → "$", per fg-country.txt).
    const symbolField = fgCountry?.config?.currency?.currency_symbol
    const symbol = (language.toUpperCase() === 'EN' ? symbolField?.english : symbolField?.native) || symbolField?.english || '$'
    const money = useCallback((n: number) => formatMoney(symbol, n), [symbol])
    const { syncCart } = useTaiwanCartSync()
    const setCart = useSetAtom(cartAtom)
    const cartEdited = useAtomValue(cartEditedAtom)
    const syncing = useAtomValue(taiwanCartSyncingAtom)
    const location = useLocation()

    // Cart edits trigger a slow server recalculation → show a skeleton while it
    // runs, but only under /checkout/* (the pages that own this summary).
    const showSkeleton = syncing && location.pathname.includes('/checkout')

    // Rehydrate the cart from the format on a fresh load (cart isn't persisted),
    // unless the user has just emptied it this session (cartEdited).
    useEffect(() => {
        if (entries.length > 0 || cartEdited) return
        const packs = readShipmentPacks(format)
        if (packs && Object.keys(packs).length > 0) setCart(packs)
    }, [entries.length, cartEdited, format, setCart])

    const { increaseV1Qty, decreaseV1Qty, removeV1Entry } = useCartMutations({
        onAfterChange: (_cart, packsJson) => syncCart(packsJson)
    })

    const { subtotal, memberDiscount } = useMemo(() => {
        let sub = 0
        let discount = 0
        for (const e of entries) {
            sub += e.unitPrice * e.qty
            if (priceMode === 'member') {
                const retail = getProductPrice(fgProducts, e.flavor, e.purchaseType, 'retail')
                if (retail > e.unitPrice) discount += (retail - e.unitPrice) * e.qty
            }
        }
        return { subtotal: sub, memberDiscount: discount }
    }, [entries, fgProducts, priceMode])

    const total = subtotal + shipping

    const nameOf = useCallback(
        (flavor: string, purchaseType: string): string => {
            const product = fgProducts[flavor]?.[purchaseType]
            return localize(product?.flavor?.titleInOrderSummary, language) || localize(product?.flavor?.text, language) || 'Feel Great'
        },
        [fgProducts, language]
    )

    if (showSkeleton) {
        return (
            <Box sx={cardSx} data-testid="taiwan-order-summary">
                <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 400, color: SUMMARY_TITLE, mb: '24px' }} data-testid="taiwan-summary-title">
                    {translate('order_summary')}:
                </Typography>
                <SummarySkeleton />
            </Box>
        )
    }

    return (
        <Box sx={cardSx} data-testid="taiwan-order-summary">
            <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 400, color: SUMMARY_TITLE, mb: '24px' }} data-testid="taiwan-summary-title">
                {translate('order_summary')}:
            </Typography>

            <Stack spacing={3} data-testid="taiwan-summary-content">
                <Stack spacing={3} data-testid="taiwan-summary-items">
                    {entries.map(e => {
                        const product = fgProducts[e.flavor]?.[e.purchaseType]
                        const image = product?.image_url
                        const name = nameOf(e.flavor, e.purchaseType)
                        const sub = localize(product?.flavor?.text, language)
                        const isSub = e.purchaseType === 'subscribe'
                        return (
                            <Stack key={`${e.purchaseType}-${e.flavor}`} direction="row" spacing={2} alignItems="flex-start" data-testid="taiwan-summary-item">
                                {image ? (
                                    <Box
                                        component="img"
                                        src={image}
                                        alt={name}
                                        sx={{ width: 74, height: 66, objectFit: 'contain', flexShrink: 0 }}
                                        onError={(ev: React.SyntheticEvent<HTMLImageElement>) => {
                                            ev.currentTarget.style.display = 'none'
                                        }}
                                        data-testid="taiwan-summary-item-image"
                                    />
                                ) : null}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack spacing={1}>
                                        <Typography
                                            sx={{
                                                fontSize: 16,
                                                lineHeight: '24px',
                                                fontWeight: 600,
                                                color: color.blue[600],
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            data-testid="taiwan-summary-item-name">
                                            {name}
                                        </Typography>
                                        {sub && sub !== name ? (
                                            <Typography
                                                sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 400, color: color.blue[600] }}
                                                data-testid="taiwan-summary-item-sub">
                                                {sub}
                                            </Typography>
                                        ) : null}
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: color.green[600] }} />
                                            <Typography
                                                sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 400, color: color.green[600] }}
                                                data-testid="taiwan-summary-item-type">
                                                {translate(isSub ? 'subscription' : 'ontime_per_bill')}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                                <Stack alignItems="flex-end" justifyContent="space-between" sx={{ height: 72, width: 96, flexShrink: 0 }}>
                                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1.5} sx={{ width: '100%' }}>
                                        <Typography
                                            sx={{ width: 64, fontSize: 16, lineHeight: '24px', fontWeight: 600, color: color.blue[600], textAlign: 'right' }}
                                            data-testid="taiwan-summary-item-price">
                                            {money(e.unitPrice * e.qty)}
                                        </Typography>
                                        {!readOnly && (
                                            <Box
                                                component="span"
                                                onClick={() => removeV1Entry(e.purchaseType, e.flavor)}
                                                sx={{ display: 'flex', flexShrink: 0, cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                                                data-testid="taiwan-summary-item-remove">
                                                <BinIcon width={20} height={20} />
                                            </Box>
                                        )}
                                    </Stack>
                                    {readOnly ? (
                                        <Box
                                            sx={{
                                                minWidth: 48,
                                                height: 32,
                                                px: 1.5,
                                                borderRadius: '20px',
                                                bgcolor: color.blue[50],
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: color.blue[600]
                                            }}
                                            data-testid="taiwan-summary-item-qty-readonly">
                                            {e.qty}
                                        </Box>
                                    ) : (
                                        <QtyControls
                                            qty={e.qty}
                                            onIncrease={() => increaseV1Qty(e.purchaseType, e.flavor)}
                                            onDecrease={() => decreaseV1Qty(e.purchaseType, e.flavor)}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        )
                    })}
                </Stack>

                <Stack spacing={1} data-testid="taiwan-summary-terms">
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 14, lineHeight: '20px', color: color.blue[600] }}>{translate('subtotal')}</Typography>
                        <Typography sx={{ fontSize: 14, lineHeight: '20px', color: color.blue[600] }} data-testid="taiwan-summary-subtotal">
                            {money(subtotal)}
                        </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 14, lineHeight: '20px', color: color.blue[600] }}>{translate('shipping')}</Typography>
                        <Typography
                            sx={{ fontSize: 14, lineHeight: '20px', color: shipping === 0 ? color.orange[500] : color.blue[600] }}
                            data-testid="taiwan-summary-shipping">
                            {shipping === 0 ? translate('free') : money(shipping)}
                        </Typography>
                    </Stack>
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: color.blue[600] }}>{translate('total_price')}:</Typography>
                    <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: color.blue[600] }} data-testid="taiwan-summary-total">
                        {money(total)}
                    </Typography>
                </Stack>

                {memberDiscount > 0 && (
                    <Stack sx={{ bgcolor: color.blue[50], borderRadius: '8px', p: '12px' }} spacing={1.5} data-testid="taiwan-summary-savings">
                        <Typography
                            sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 600, color: SAVINGS_TEXT, letterSpacing: '-0.07px' }}
                            data-testid="taiwan-summary-savings-title">
                            {translate('your_savings')}
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontSize: 14, fontWeight: 400, color: SAVINGS_TEXT }}>{translate('member_discount')}</Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <TagIcon width={12} height={12} />
                                <Typography
                                    sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: DISCOUNT, textAlign: 'right' }}
                                    data-testid="taiwan-summary-member-discount">
                                    -{money(memberDiscount)}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                )}

                {footer}
            </Stack>
        </Box>
    )
}
